import { useSelector } from 'react-redux';
import schemas from '@/validations';
import { PAYMENT_METHOD_TYPE } from '@/models/payment';
import { SUBSCRIPTION_STATUS } from '@/models/subscription';
import { PLAN_TYPE } from '@/const/plans';
import { ROLES } from '@/pages/private/users/constants';

import { useShallowSelector } from './redux';

/**
 * Use this hook to know user current payment method details.
 */
export function usePaymentMethod() {
	const { cardLast4, cardBrand, type, cardType, linkEmail, walletType } =
		useSelector(state => state.auth?.paymentMethod) || {};

	const hasPaymentMethodMap = {
		[PAYMENT_METHOD_TYPE.CARD]: !!cardLast4,
		[PAYMENT_METHOD_TYPE.PAYPAL]: true,
		[PAYMENT_METHOD_TYPE.LINK]: true
	};

	return {
		hasPaymentMethod: !!hasPaymentMethodMap[type],
		info: {
			cardLast4,
			cardBrand,
			cardType,
			type,
			linkEmail,
			walletType
		}
	};
}

/**
 * Use this hook to interact with billing data logic.
 */
export function useTaxInformation() {
	const data =
		useShallowSelector(state => ({
			email: state.auth?.user?.email,
			...state.auth?.user?.taxInformation
		})) || {};

	return {
		hasContent: Object.keys(data).length > 1,
		isValid: schemas.taxInformation({ sync: true }).isValidSync(data),
		data
	};
}

export function useShowCurrencySelector() {
	const subscriptionStatus = useSelector(
		state => state.auth.user?.subscription?.status
	);

	return (
		!subscriptionStatus ||
		[SUBSCRIPTION_STATUS.CANCELED, SUBSCRIPTION_STATUS.TRIALING].includes(
			subscriptionStatus
		)
	);
}

export function useSubscribed() {
	const subscriptionStatus = useSelector(
		state =>
			state.auth.subscriptionInfo?.status ||
			state.auth.user?.subscription?.status
	);

	return (
		subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE ||
		subscriptionStatus === SUBSCRIPTION_STATUS.TRIALING
	);
}

export function useHasStorageCharge() {
	return useSelector(state => state.auth?.user?.hasStorageServiceCharge);
}

export function useIsTrialing() {
	const subscriptionStatus = useSelector(
		state => state.auth.user?.subscription?.status
	);

	return subscriptionStatus === SUBSCRIPTION_STATUS.TRIALING;
}

export function useTrialDiscount() {
	return useSelector(state => state.auth?.user?.trialDiscount);
}

export function useUser() {
	const user = useSelector(state => state.auth.user);

	return user;
}

export function useSubscriptionInfo() {
	const subscriptionInfo = useSelector(state => state.auth.subscriptionInfo);

	const { status, plan, cancellationRequestAt } = subscriptionInfo || {};

	const isTrial =
		plan === PLAN_TYPE.TRIAL || status === SUBSCRIPTION_STATUS.TRIALING;

	let canUseTrial = true;

	if (typeof subscriptionInfo?.canUseTrial !== 'undefined') {
		canUseTrial = subscriptionInfo.canUseTrial;
	}

	return {
		...subscriptionInfo,
		cancellationRequested: !!cancellationRequestAt,
		status,
		plan,
		parsedPlan: isTrial ? 'trial' : plan,
		isTrial,
		isExpired: status === SUBSCRIPTION_STATUS.EXPIRED,
		canUseTrial,
		isCanceled: status === SUBSCRIPTION_STATUS.CANCELED,
		isPending: status === SUBSCRIPTION_STATUS.PENDING
	};
}

export function useIsOwner() {
	return !useSelector(state => state.auth?.user?.parentAccount);
}

export function useIsCollaborator() {
	return useSelector(state => state.auth.user?.role === ROLES.COLLABORATOR);
}

export function useIsExtendedCollaborator() {
	return useSelector(state => state.auth.user?.role === ROLES.LIMITED);
}
